import { Box, Center, Loader, Text } from '@mantine/core';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useCalendarEventMeetingAgenda } from 'src/meetings/hooks/useCalendarEventMeetingAgenda';
import { useGenerateCalendarEventMeetingAgenda } from 'src/meetings/hooks/useGenerateCalendarEventMeetingAgenda';
import type { CalendarEvent } from 'src/meetings/models';
import { getFirstClientParticipant, getParticipantFullName } from '../utils/event-utils';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DownloadFileButton } from 'src/common/components/buttons/DownloadFileButton';

type Props = {
  event: CalendarEvent;
};

export const MeetingAgendaCard = ({ event }: Props) => {
  const { files: templates } = useTemplates({
    templateType: 'MEETING_AGENDA',
  });

  const {
    meetingAgenda,
    isLoading: isLoadingAgenda,
    refetch,
  } = useCalendarEventMeetingAgenda({ eventId: event.id, enabled: event.hasMeetingAgenda });

  const { generateMeetingAgenda, isLoading: isGenerating } = useGenerateCalendarEventMeetingAgenda(
    event.id,
  );

  const defaultTemplate = templates.length > 0 ? templates[0] : null;

  const clientParticipant = getFirstClientParticipant(event);
  const clientName = getParticipantFullName(clientParticipant) ?? 'the client';

  const isBackgroundProcessing =
    meetingAgenda?.backgroundProcess?.status === 'PENDING' ||
    meetingAgenda?.backgroundProcess?.status === 'PROCESSING';

  const isSuccess = meetingAgenda?.backgroundProcess?.status === 'SUCCESS';

  const handleGenerate = async () => {
    if (!defaultTemplate) return;

    try {
      await generateMeetingAgenda({ templateId: defaultTemplate.id });
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to generate meeting agenda', 'Please try again later', error, {
        eventId: event.id,
        templateId: defaultTemplate.id,
      });
    }
  };

  if (defaultTemplate == null) {
    return null;
  }

  const isInPast = event.endAt != null && new Date(event.endAt) < new Date();
  if (
    (isLoadingAgenda || isInPast) &&
    (!meetingAgenda || meetingAgenda.backgroundProcess?.status === 'FAILED')
  ) {
    return null;
  }

  if (isBackgroundProcessing || isGenerating) {
    return (
      <Box bg="gray.1" p="lg" style={{ borderRadius: '8px' }}>
        <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="sm">
          Meeting agenda
        </Text>
        <Center>
          <Loader />
        </Center>
      </Box>
    );
  }

  if (isSuccess && meetingAgenda?.markdown) {
    return (
      <Box style={{ position: 'relative' }}>
        <Box
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            margin: 8,
            zIndex: 1,
          }}
        >
          <DownloadFileButton variant={'subtle'} fileId={meetingAgenda.fileId!} color={'black'} />
        </Box>
        <MarkdownEditor defaultValue={meetingAgenda.markdown} readonly={true} h="100%" />
      </Box>
    );
  }

  return (
    <Box bg="gray.1" p="lg" style={{ borderRadius: '8px' }}>
      <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10" mb="sm">
        Meeting agenda
      </Text>

      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
        <Text component="span" c="brand.6" style={{ cursor: 'pointer' }} onClick={handleGenerate}>
          Generate a personalised agenda
        </Text>{' '}
        based on {clientName}&apos;s profile
      </Text>
    </Box>
  );
};
